import React, { useState, useEffect } from 'react';
import { Vacancy } from '../types';
import { Linkedin, Mail, Facebook, Twitter, MessageCircle, Copy, Check, Send, ExternalLink, Share2, Wand2, RefreshCw } from 'lucide-react';

interface VacancyNetworkShareProps {
    vacancy: Vacancy;
    isRegenerating?: boolean;
    onRegenerate?: (category: 'linkedinPost' | 'whatsappMessage' | 'inMail') => void;
    onUpdate?: (updates: any) => void;
    isReadOnly?: boolean;
}

const VacancyNetworkShare: React.FC<VacancyNetworkShareProps> = ({ vacancy, isRegenerating, onRegenerate }) => {
    const [activeTab, setActiveTab] = useState<'socials' | 'whatsapp' | 'email'>('socials');

    // Local state to hold the editable messages before sharing
    const [linkedinMsg, setLinkedinMsg] = useState(
        vacancy.data?.structuredTemplates?.linkedinPost?.items?.[0]?.content || `Nieuwe vacature: ${vacancy.title} bij ${vacancy.client?.name || 'ons team'}! Bekijk hem hier: ${window.location.origin}/vacancy/${vacancy.id}`
    );

    const [whatsappMsg, setWhatsappMsg] = useState(
        vacancy.data?.structuredTemplates?.whatsappMessage?.items?.[0]?.content || `Hoi! We zoeken een ${vacancy.title} bij ${vacancy.client?.name || 'ons team'}. Misschien iets voor jou of iemand in je netwerk? ${window.location.origin}/vacancy/${vacancy.id}`
    );

    const [emailMsg, setEmailMsg] = useState(
        vacancy.data?.structuredTemplates?.inMail?.items?.[0]?.content || `Hoi,\n\nWe zijn voor ${vacancy.client?.name || 'ons team'} op zoek naar een ${vacancy.title}.\n\nBekijk de vacature hier: ${window.location.origin}/vacancy/${vacancy.id}\n\nGroet,`
    );

    // Update local state when the underlying template updates after regeneration
    useEffect(() => {
        setLinkedinMsg(vacancy.data?.structuredTemplates?.linkedinPost?.items?.[0]?.content || linkedinMsg);
        setWhatsappMsg(vacancy.data?.structuredTemplates?.whatsappMessage?.items?.[0]?.content || whatsappMsg);
        setEmailMsg(vacancy.data?.structuredTemplates?.inMail?.items?.[0]?.content || emailMsg);
    }, [vacancy.data?.structuredTemplates]);

    const [emailTo, setEmailTo] = useState('');
    const [copied, setCopied] = useState<'linkedin' | 'whatsapp' | 'email' | null>(null);

    const handleCopy = (text: string, type: 'linkedin' | 'whatsapp' | 'email') => {
        navigator.clipboard.writeText(text);
        setCopied(type);
        setTimeout(() => setCopied(null), 2000);
    };

    const shareUrl = `${window.location.origin}/vacancy/${vacancy.id}`;

    const openShare = (platform: 'linkedin' | 'facebook' | 'twitter') => {
        let url = '';
        const encodedUrl = encodeURIComponent(shareUrl);
        const encodedText = encodeURIComponent(linkedinMsg.substring(0, 200)); // Some platforms limit text/don't support it well via URL

        switch (platform) {
            case 'linkedin':
                // LinkedIn share URL format (simplified)
                url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
                break;
            case 'facebook':
                url = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
                break;
            case 'twitter':
                url = `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedText}`;
                break;
        }
        window.open(url, '_blank', 'width=600,height=400');
    };

    return (
        <div className="max-w-5xl mx-auto space-y-6 animate-fadeIn pb-12">
            <div className="flex flex-col items-center mb-10 relative">
                <div className="w-20 h-20 bg-emerald-50 rounded-full flex items-center justify-center mb-6 text-emerald-500 shadow-inner">
                    <Share2 size={32} />
                </div>
                <h2 className="text-3xl font-black text-slate-900 uppercase tracking-tight mb-4 flex items-center gap-3">
                    Deel in je Netwerk
                </h2>
                <p className="text-slate-500 font-medium max-w-xl text-center text-lg leading-relaxed mb-6">
                    Gebruik AI templates om deze vacature snel en effectief te delen via Social Media, WhatsApp of gerichte E-mails.
                </p>

                {onRegenerate && (
                    <button
                        onClick={() => {
                            if (activeTab === 'socials') onRegenerate('linkedinPost');
                            if (activeTab === 'whatsapp') onRegenerate('whatsappMessage');
                            if (activeTab === 'email') onRegenerate('inMail');
                        }}
                        disabled={isRegenerating}
                        className="flex items-center justify-center gap-2 bg-gradient-to-r from-red- to-indigo-600 hover:from-red- hover:to-indigo-700 text-white px-5 py-2.5 rounded-full font-black text-xs uppercase tracking-widest shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5"
                    >
                        {isRegenerating ? <RefreshCw size={14} className="animate-spin" /> : <Wand2 size={14} />}
                        {isRegenerating ? `Herschrijven...` : `Laat Froukje Herschrijven`}
                    </button>
                )}
            </div>

            <div className="bg-white rounded-[2rem] border border-slate-200 shadow-xl overflow-hidden flex flex-col md:flex-row">

                {/* Sidebar Nav */}
                <div className="w-full md:w-64 bg-slate-50 border-r border-slate-200 flex flex-row md:flex-col p-4 gap-2 overflow-x-auto no-scrollbar shrink-0">
                    <button
                        onClick={() => setActiveTab('socials')}
                        className={`flex items-center gap-3 px-4 py-3 rounded-xl font-bold text-sm transition-all whitespace-nowrap ${activeTab === 'socials' ? 'bg-white shadow-sm text-red- border border-slate-200' : 'text-slate-500 hover:bg-slate-100'
                            }`}
                    >
                        <Linkedin size={18} /> Social Media
                    </button>

                    <button
                        onClick={() => setActiveTab('whatsapp')}
                        className={`flex items-center gap-3 px-4 py-3 rounded-xl font-bold text-sm transition-all whitespace-nowrap ${activeTab === 'whatsapp' ? 'bg-white shadow-sm text-green-600 border border-slate-200' : 'text-slate-500 hover:bg-slate-100'
                            }`}
                    >
                        <MessageCircle size={18} /> WhatsApp
                    </button>

                    <button
                        onClick={() => setActiveTab('email')}
                        className={`flex items-center gap-3 px-4 py-3 rounded-xl font-bold text-sm transition-all whitespace-nowrap ${activeTab === 'email' ? 'bg-white shadow-sm text-red-600 border border-slate-200' : 'text-slate-500 hover:bg-slate-100'
                            }`}
                    >
                        <Mail size={18} /> Directe E-mail
                    </button>
                </div>

                {/* Content Area */}
                <div className="flex-1 p-8">

                    {/* SOCIALS */}
                    {activeTab === 'socials' && (
                        <div className="space-y-6 animate-fadeIn">
                            <div className="flex items-center justify-between">
                                <h3 className="text-xl font-black text-slate-900 tracking-tight">Social Media Campagne</h3>
                                <button
                                    onClick={() => handleCopy(linkedinMsg, 'linkedin')}
                                    className="flex items-center gap-2 text-xs font-bold text-slate-500 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 px-3 py-1.5 rounded-lg transition-colors"
                                >
                                    {copied === 'linkedin' ? <Check size={14} className="text-emerald-500" /> : <Copy size={14} />} Kopieer Tekst
                                </button>
                            </div>

                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Bericht (Pas aan waar nodig)</label>
                                <textarea
                                    value={linkedinMsg}
                                    onChange={(e) => setLinkedinMsg(e.target.value)}
                                    className="w-full h-48 p-4 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-700 focus:ring-2 focus:ring-red-0 focus:border-red-0 transition-all resize-none"
                                />
                                <p className="text-xs text-slate-500 italic mt-2 text-right">{linkedinMsg.length} tekens</p>
                            </div>

                            <div className="p-6 bg-red-/50 rounded-2xl border border-red-">
                                <p className="text-sm font-bold text-red- mb-4 text-center">Deel direct naar je tijdlijn:</p>
                                <div className="flex justify-center gap-4">
                                    <button onClick={() => openShare('linkedin')} className="flex flex-col items-center gap-2 group">
                                        <div className="w-14 h-14 bg-[#0077b5] text-white rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform"><Linkedin size={24} /></div>
                                        <span className="text-xs font-bold text-[#0077b5]">LinkedIn</span>
                                    </button>
                                    <button onClick={() => openShare('facebook')} className="flex flex-col items-center gap-2 group">
                                        <div className="w-14 h-14 bg-[#1877F2] text-white rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform"><Facebook size={24} /></div>
                                        <span className="text-xs font-bold text-[#1877F2]">Facebook</span>
                                    </button>
                                    <button onClick={() => openShare('twitter')} className="flex flex-col items-center gap-2 group">
                                        <div className="w-14 h-14 bg-[#1DA1F2] text-white rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform"><Twitter size={24} /></div>
                                        <span className="text-xs font-bold text-[#1DA1F2]">X (Twitter)</span>
                                    </button>
                                </div>
                                <p className="text-[10px] text-center text-red-/70 mt-4 leading-tight">
                                    Tip: Bij sommige netwerken moet je de tekst zelf nog even plakken nadat de link opent.
                                </p>
                            </div>
                        </div>
                    )}

                    {/* WHATSAPP */}
                    {activeTab === 'whatsapp' && (
                        <div className="space-y-6 animate-fadeIn">
                            <div className="flex items-center justify-between">
                                <h3 className="text-xl font-black text-slate-900 tracking-tight">Deel via WhatsApp</h3>
                                <button
                                    onClick={() => handleCopy(whatsappMsg, 'whatsapp')}
                                    className="flex items-center gap-2 text-xs font-bold text-slate-500 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 px-3 py-1.5 rounded-lg transition-colors"
                                >
                                    {copied === 'whatsapp' ? <Check size={14} className="text-emerald-500" /> : <Copy size={14} />} Kopieer Tekst
                                </button>
                            </div>

                            <div className="bg-[#E7F4E4] p-6 rounded-2xl relative border border-[#D1ECCD]">
                                {/* CSS bubble tail mock */}
                                <div className="absolute -left-2 top-6 w-4 h-4 bg-[#E7F4E4] transform rotate-45 border-l border-b border-[#D1ECCD]"></div>

                                <label className="text-[10px] font-black text-green-800/60 uppercase tracking-widest mb-2 block">Jouw Bericht</label>
                                <textarea
                                    value={whatsappMsg}
                                    onChange={(e) => setWhatsappMsg(e.target.value)}
                                    className="w-full min-h-[120px] p-3 bg-white/60 border border-green-200 rounded-xl text-sm text-slate-800 focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all resize-none shadow-inner"
                                />
                            </div>

                            <div className="flex justify-end">
                                <a
                                    href={`https://wa.me/?text=${encodeURIComponent(whatsappMsg)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center justify-center gap-3 bg-[#25D366] hover:bg-[#1DA851] text-white px-8 py-4 rounded-xl font-black text-sm uppercase tracking-widest transition-all shadow-lg hover:shadow-xl hover:-translate-y-1 w-full md:w-auto"
                                >
                                    <MessageCircle size={20} /> Open in WhatsApp
                                </a>
                            </div>
                        </div>
                    )}

                    {/* EMAIL */}
                    {activeTab === 'email' && (
                        <div className="space-y-6 animate-fadeIn">
                            <div className="flex items-center justify-between">
                                <h3 className="text-xl font-black text-slate-900 tracking-tight">Persoonlijke Uitnodiging</h3>
                                <button
                                    onClick={() => handleCopy(emailMsg, 'email')}
                                    className="flex items-center gap-2 text-xs font-bold text-slate-500 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 px-3 py-1.5 rounded-lg transition-colors"
                                >
                                    {copied === 'email' ? <Check size={14} className="text-emerald-500" /> : <Copy size={14} />} Kopieer
                                </button>
                            </div>
                            <p className="text-sm text-slate-500">Stuur een persoonlijke e-mail naar een specifieke kandidaat of contactpersoon vanuit je eigen mailprogramma.</p>

                            <div className="space-y-4 bg-slate-50 p-6 rounded-2xl border border-slate-200">
                                <div>
                                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Aan (Optioneel)</label>
                                    <input
                                        type="email"
                                        placeholder="bijv. kandidaat@email.com"
                                        value={emailTo}
                                        onChange={(e) => setEmailTo(e.target.value)}
                                        className="w-full mt-1 p-3 bg-white border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500 outline-none"
                                    />
                                </div>

                                <div>
                                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Onderwerp</label>
                                    <input
                                        type="text"
                                        readOnly
                                        value={`Interessante vacature: ${vacancy.title}`}
                                        className="w-full mt-1 p-3 bg-white border border-slate-200 rounded-lg text-sm text-slate-500 cursor-not-allowed"
                                    />
                                </div>

                                <div>
                                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Bericht</label>
                                    <textarea
                                        value={emailMsg}
                                        onChange={(e) => setEmailMsg(e.target.value)}
                                        className="w-full h-40 mt-1 p-4 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all resize-none shadow-inner"
                                    />
                                </div>
                            </div>

                            <div className="flex justify-end">
                                <a
                                    href={`mailto:${emailTo}?subject=${encodeURIComponent(`Interessante vacature: ${vacancy.title}`)}&body=${encodeURIComponent(emailMsg)}`}
                                    className="flex items-center justify-center gap-3 bg-slate-900 hover:bg-black text-white px-8 py-4 rounded-xl font-black text-sm uppercase tracking-widest transition-all shadow-lg hover:shadow-xl hover:-translate-y-1 w-full md:w-auto"
                                >
                                    <Send size={18} /> Open Mailprogramma
                                </a>
                            </div>
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
};

export default VacancyNetworkShare;
